/**
 * Admin-app NotificationCenter (W0.8).
 *
 * Identical pattern to the customer-app NotificationCenter — a global
 * toast queue exposed via `useNotifications()`.
 *
 * As of 2026-05-11 (cold-start mitigation work), retry-status events are
 * surfaced via `ServerWakingBanner` (the dedicated top-of-page banner)
 * rather than the toast stack — two simultaneous surfaces for the same
 * state was confusing. This component remains the general toast queue
 * for everything else (success/info/error notifications from page code).
 *
 * Separate file (not shared with customer-app) because each app's
 * lib/api.ts has its own `onRetryStatus` listener registry — sharing
 * would cross the app boundary and risk leaking customer toasts into
 * the staff portal.
 */

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { Toast, type ToastType } from '@ferro/ui';

const MAX_VISIBLE = 3;
const DEFAULT_DURATION_MS = 3000;

export interface NotificationItem {
  readonly id: string;
  readonly message: string;
  readonly type: ToastType;
  readonly duration: number;
}

export interface NotificationContextValue {
  notify(message: string, type?: ToastType, duration?: number): string;
  dismiss(id: string): void;
  readonly notifications: ReadonlyArray<NotificationItem>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

let nextId = 0;
function makeId(): string {
  nextId += 1;
  return `admin-toast-${nextId}-${Date.now().toString(36)}`;
}

export function NotificationProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const dismiss = useCallback((id: string): void => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      duration: number = DEFAULT_DURATION_MS,
    ): string => {
      const id = makeId();
      setNotifications((prev) => {
        const trimmed =
          prev.length >= MAX_VISIBLE
            ? prev.slice(prev.length - (MAX_VISIBLE - 1))
            : prev;
        return [...trimmed, { id, message, type, duration }];
      });
      return id;
    },
    [],
  );

  const value: NotificationContextValue = {
    notify,
    dismiss,
    notifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationStack notifications={notifications} dismiss={dismiss} />
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}

function NotificationStack({
  notifications,
  dismiss,
}: {
  readonly notifications: ReadonlyArray<NotificationItem>;
  readonly dismiss: (id: string) => void;
}): JSX.Element {
  return (
    <div
      className="ferro-toast-stack"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {notifications.map((n) => (
        <Toast
          key={n.id}
          message={n.message}
          isVisible
          type={n.type}
          duration={n.duration}
          onHide={() => dismiss(n.id)}
        />
      ))}
    </div>
  );
}
